import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useApolloClient } from '@apollo/client';
import { bookingShape } from 'constants/propTypes';
import { FormattedMessage } from 'react-intl';
import { useForm } from 'react-hook-form';
import Modal from 'components/ui/Modal';
import TextInput from 'components/ui/TextInput';
import { CancelButton, ConfirmButton } from 'components/ui/Button';
import { ADD_EXTERNAL_REFERENCE, ADD_NOTE_TO_BOOKING } from 'store/mutations';
import { GET_BOOKING } from 'store/queries';
import { useManagedMutation } from 'utils/managedHooks';
import AgentPortalError from 'errors/AgentPortalError';
import { StyledConfirmButton, ErrorMessage, StyledAddMasterReferenceConfirmation } from './ExternalReferencesPopup.styles';
import { getResarailBookingInfos } from './ExternalReferencesPopup.utils';
import {
  getConfirmedNonEurostarExternalReferenceProducts,
  filterExternalReferencesByExternalReferenceProducts
} from '../BookingDetailsTable.utils';

export const MASTER_BOOKING_REFERENCE_SOURCE = 'MASTER';
export const PAO_BOOKING_REFERENCE_SOURCE = 'PAO';
export const BENERAIL_BOOKING_REFERENCE_SOURCE = 'benerail';

const BOOKING_NOT_FOUND_INVENTORY_ERROR_CODES = ['could_not_find_booking', 'not_found', '3016'];

const isBookingNotFoundError = (error) =>
  error?.graphQLErrors?.some(({ extensions }) => {
    const inventoryErrorCode = String(extensions?.inventoryErrorCode);
    return extensions?.code === 'INVALID_VARIABLE' || BOOKING_NOT_FOUND_INVENTORY_ERROR_CODES.includes(inventoryErrorCode);
  });

const ExternalReferencesPopup = ({ booking, isOpen, toggleOpen }) => {
  const { externalBookingReferences = [] } = booking;
  const [masterReference, setMasterReference] = useState('');
  const [isMasterReferencePnr, setIsMasterReferencePnr] = useState(false);
  const [isCheckingMasterReference, setIsCheckingMasterReference] = useState(false);
  const [bookingLookupError, setBookingLookupError] = useState(null);
  const apolloClient = useApolloClient();
  const hasMasterReference = externalBookingReferences.find((ref) => ref.source === MASTER_BOOKING_REFERENCE_SOURCE);
  const { register, handleSubmit, resetField, formState } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const { errors } = formState;

  const [addExternalReference, { loading: addExternalReferenceLoading }] = useManagedMutation(ADD_EXTERNAL_REFERENCE, {
    refetchQueries: ['booking']
  });

  const [addNote, { loading: addNoteLoading }] = useManagedMutation(ADD_NOTE_TO_BOOKING);

  const closeModal = () => {
    setMasterReference('');
    setIsMasterReferencePnr(false);
    setIsCheckingMasterReference(false);
    setBookingLookupError(null);
    toggleOpen(false);
  };

  const saveMasterReference = async (data) => {
    setIsCheckingMasterReference(true);
    setBookingLookupError(null);

    try {
      const { data: bookingData } = await apolloClient.query({
        query: GET_BOOKING,
        variables: {
          reference: data.masterReference
        },
        fetchPolicy: 'network-only'
      });
      setIsMasterReferencePnr(Boolean(bookingData?.booking));
      setMasterReference(data.masterReference);
    } catch (error) {
      if (isBookingNotFoundError(error)) {
        setIsMasterReferencePnr(false);
        setMasterReference(data.masterReference);
      } else {
        setBookingLookupError(error);
      }
    } finally {
      setIsCheckingMasterReference(false);
    }
  };

  const returnToMasterReferenceForm = () => {
    setMasterReference('');
    setIsMasterReferencePnr(false);
    setIsCheckingMasterReference(false);
    setBookingLookupError(null);
    resetField('masterReference');
  };

  const submitMasterReference = async () => {
    await addExternalReference({
      variables: {
        reference: booking.reference,
        externalReference: {
          id: masterReference,
          source: MASTER_BOOKING_REFERENCE_SOURCE,
          master: true
        }
      }
    });

    await addNote({
      variables: {
        note: `Master reference added: ${masterReference}`,
        bookingReference: booking.reference
      }
    });

    closeModal();
  };

  const resarailInfos = getResarailBookingInfos({ booking });
  const confirmedNonEurostarExternalReferenceProducts = getConfirmedNonEurostarExternalReferenceProducts({ booking });
  const filteredExternalReferences = filterExternalReferencesByExternalReferenceProducts({
    externalBookingReferences,
    confirmedNonEurostarExternalReferenceProducts
  });

  if (bookingLookupError) {
    throw new AgentPortalError(bookingLookupError);
  }

  return (
    <Modal isOpen={isOpen} title={<FormattedMessage id="bookingDetails.bookingExternalReferences" />} onClose={closeModal}>
      {!externalBookingReferences.length && !masterReference && !resarailInfos.length && (
        <FormattedMessage id="bookingDetails.noBookingExternalReferencesMessage" />
      )}
      {!!(externalBookingReferences.length || masterReference) && (
        <div data-testid="external-references-list">
          {filteredExternalReferences.map(({ source, externalId }) => {
            const isPAO = source === 'PAO';
            return (
              !isPAO && (
                <div key={externalId}>
                  <strong>{source === 'benerail' ? 'BeNe Rail PNR' : source}</strong>:{' '}
                  {source === MASTER_BOOKING_REFERENCE_SOURCE ? (
                    <a href={`/bookings?reference=${externalId}`}>{externalId}</a>
                  ) : (
                    <span>{externalId}</span>
                  )}
                </div>
              )
            );
          })}
          {resarailInfos.length > 0 && (
            <div data-testid="resarail-infos-list">
              {resarailInfos.map((value) => (
                <div key={value.key}>
                  <strong>{value.name} : </strong>
                  <span>{value.PNR} : </span>
                  <span>{`${value.origin} to ${value.destination}`}</span>
                </div>
              ))}
            </div>
          )}
          {masterReference && (
            <div data-testid="added-master-reference">
              <strong>{MASTER_BOOKING_REFERENCE_SOURCE}</strong>: <span>{masterReference}</span>
            </div>
          )}
        </div>
      )}
      {!hasMasterReference && (
        <>
          {!masterReference && (
            <form onSubmit={handleSubmit(saveMasterReference)} data-testid="add-master-reference-form">
              <TextInput
                type="text"
                data-testid="master-reference"
                maxLength={25}
                disabled={isCheckingMasterReference}
                {...register('masterReference', {
                  required: true,
                  pattern: /^[a-zA-Z0-9_ "'-]{5,25}$/
                })}
              />
              <StyledConfirmButton loading={isCheckingMasterReference} disabled={!formState.isValid || isCheckingMasterReference}>
                <FormattedMessage id="bookingDetails.addMasterReference" />
              </StyledConfirmButton>
              {errors.masterReference && (
                <ErrorMessage>
                  <FormattedMessage id="bookingDetails.masterReferenceValidationError" />
                </ErrorMessage>
              )}
            </form>
          )}
          {masterReference && (
            <StyledAddMasterReferenceConfirmation>
              <div>
                {isMasterReferencePnr && (
                  <CancelButton
                    type="button"
                    margin="16px 16px 16px 0"
                    data-testid="master-reference-back"
                    onClick={returnToMasterReferenceForm}
                  >
                    <FormattedMessage id="bookingDetails.masterReferenceBack" />
                  </CancelButton>
                )}
                <ConfirmButton
                  loading={addExternalReferenceLoading || addNoteLoading}
                  disabled={isMasterReferencePnr || isCheckingMasterReference || addExternalReferenceLoading || addNoteLoading}
                  onClick={submitMasterReference}
                >
                  <FormattedMessage id="bookingDetails.confirm" />
                </ConfirmButton>
              </div>
              <div>
                {isMasterReferencePnr ? (
                  <ErrorMessage data-testid="master-reference-pnr-error">
                    <FormattedMessage id="bookingDetails.masterReferencePnrError" />
                  </ErrorMessage>
                ) : (
                  <FormattedMessage id="bookingDetails.masterReferenceConfirmationWarning" />
                )}
              </div>
            </StyledAddMasterReferenceConfirmation>
          )}
        </>
      )}
    </Modal>
  );
};

ExternalReferencesPopup.propTypes = {
  booking: bookingShape,
  isOpen: PropTypes.bool,
  toggleOpen: PropTypes.func
};

export default ExternalReferencesPopup;
